// ActionQueueRow — one row of the Today landing page action queue.
//
// Single horizontal card-row showing an actionable item for ONE (shop,
// action_type) pair. Phase 3 of the MEC UX overhaul. Click navigates
// up to Today.jsx, which routes to /mec/location/{shop_id}?... with a
// filter hint that Phase 4 will consume.
//
// Layout:
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ [icon]  Shop Name             count                $X,XXX  ●  →  │
//   │         action label                            urgency dot      │
//   └──────────────────────────────────────────────────────────────────┘

import {
  Receipt, AlertTriangle, CreditCard, MessageSquare, ListChecks,
  ArrowRight, HelpCircle,
} from 'lucide-react';

import Badge from '../ui/Badge';
import { formatMoney } from '../../utils/formatters';

// Action-type → icon. Matches the keys defined in Today.jsx
// ACTION_DRILLDOWN_FILTER (and re-used as `action_type` prop here).
const ACTION_ICON = {
  bills_missing:    Receipt,
  over_billed:      AlertTriangle,
  credits_expected: CreditCard,
  memo_gap:         MessageSquare,
  tier_2_pending:   ListChecks,
};

// Urgency-tier visual mapping. Reuses the project's warm palette so the
// row reads consistent with Dashboard / Locations chrome.
const URGENCY_STYLE = {
  high: {
    dot:   'bg-[var(--color-danger-500)]',
    ring:  'group-hover:border-[var(--color-danger-200)]',
    iconBg:'bg-[var(--color-danger-50)]',
    iconFg:'text-[var(--color-danger-600)]',
    badge: 'danger',
  },
  medium: {
    dot:   'bg-[var(--color-action-500)]',
    ring:  'group-hover:border-[var(--color-action-200)]',
    iconBg:'bg-[var(--color-action-50)]',
    iconFg:'text-[var(--color-action-600)]',
    badge: 'action',
  },
  low: {
    dot:   'bg-[var(--color-insight-500)]',
    ring:  'group-hover:border-[var(--color-insight-200)]',
    iconBg:'bg-[var(--color-insight-50)]',
    iconFg:'text-[var(--color-insight-600)]',
    badge: 'insight',
  },
};

export default function ActionQueueRow({
  shop_name,
  shop_id,
  action_type,
  action_label,
  count = 0,
  dollars_cents = 0,
  urgency = 'low',
  period_start,
  period_end,
  onClick,
}) {
  const Icon = ACTION_ICON[action_type] || HelpCircle;
  const style = URGENCY_STYLE[urgency] || URGENCY_STYLE.low;
  const dollars = formatMoney((dollars_cents || 0) / 100);

  return (
    <button
      type="button"
      onClick={onClick}
      data-shop-id={shop_id}
      data-action-type={action_type}
      data-period-start={period_start}
      data-period-end={period_end}
      className={`
        group w-full text-left
        flex items-center gap-4
        bg-[var(--color-cream-50)]
        border border-[var(--color-warm-100)]
        rounded-[var(--radius-lg)]
        shadow-[var(--shadow-xs)]
        px-5 py-4
        transition-all duration-150 ease-out
        hover:shadow-[var(--shadow-md)]
        hover:bg-[var(--color-cream-100)]
        ${style.ring}
        focus:outline-none focus:ring-2 focus:ring-[var(--color-warm-300)]
      `}
    >
      {/* Icon disc */}
      <div className={`
        shrink-0 w-10 h-10 rounded-[var(--radius-md)]
        ${style.iconBg} ${style.iconFg}
        flex items-center justify-center
      `}>
        <Icon size={18} />
      </div>

      {/* Middle: shop + action label + count */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-semibold text-[var(--color-warm-900)] truncate">
            {shop_name}
          </span>
          <Badge tone={style.badge} size="xs">
            {count} {count === 1 ? 'item' : 'items'}
          </Badge>
        </div>
        <div className="text-[12px] text-[var(--color-warm-500)] mt-0.5 truncate">
          {action_label}
        </div>
      </div>

      {/* Right: dollars + urgency dot + CTA */}
      <div className="shrink-0 flex items-center gap-4">
        <div className="text-right">
          <div className="text-[15px] font-bold text-[var(--color-warm-900)] tabular-nums">
            {dollars}
          </div>
          <div className="flex items-center justify-end gap-1.5 text-[11px] text-[var(--color-warm-500)] mt-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
            <span className="capitalize">{urgency}</span>
          </div>
        </div>
        <div className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--color-warm-700)] group-hover:text-[var(--color-warm-900)] transition-colors">
          Open
          <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </button>
  );
}
